/**
 * Pure display contract used only inside PDF utility methods for rendering a
 * single signature row (name cell, date cell, text/signature cell).
 * No business logic — callers pre-compute the values to display.
 */
export interface SignatureRowOptions {
  /** Label for the signature row (e.g. 'Applicant Name'). Defaults to 'Applicant Name' when omitted. */
  role?: string;
  fullName?: string;
  /** Pre-filled date value. Pass `true` for the current date/time, a specific Date, or omit for a blank field. */
  signedDate?: Date | true;
  /** Text shown in the signature column. Omit to render a blank underline. */
  signedText?: string;
}

/**
 * Signing outcome used where business decisions are made (cover-page status icons,
 * consent-text visibility). Extends SignatureRowOptions so it can be passed
 * anywhere a SignatureRowOptions is expected.
 *
 * - 'digital'   → verified Alberta.ca digital signature (check ✓ on cover page)
 * - 'uploaded'  → physical consent document was uploaded (upload icon on cover page)
 * - 'pending'   → signing process started but not yet complete (X on cover page)
 * - 'none'      → no signing action taken (X on cover page)
 */
export type SigningMethod = 'digital' | 'uploaded' | 'pending' | 'none';

export interface SignerInfo extends SignatureRowOptions {
  signingMethod: SigningMethod;
  /**
   * True when the spouse selected the 'download and print' signing mode.
   * Used by templates to decide which document list to display.
   * Distinct from signingMethod (outcome) — a print-mode signer may be 'uploaded' or 'pending'.
   */
  isDownloadAndPrint?: boolean;
}
